import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import SortIcon from '@material-ui/icons/Sort';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import FBDropdownButton from './Component';
import FBToggleList from './Component/mobile';
import { colors, fontSize } from '../../constants/styleConstants';
import { TP } from '../../constants/consts';
import { useTranslation } from '../../src/i18n';
import { getOptions } from '../InteractiveFilterPanel/aggregationUtil';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  label: {
    fontSize: fontSize.smallTitle,
    color: colors.darkText,
    textTransform: 'uppercase',
    marginRight: '8px',
    opacity: '0.6',
  },
  labelSort: {
    fontSize: fontSize.smallTitle,
    color: colors.darkText,
    marginRight: '8px',
    opacity: '0.6',
  },
  fullList: {
    display: 'flex',
    minHeight: '320px',
    maxHeight: '320px',
  },
  list1: {
    width: '40%',
  },
  list2: {
    width: '60%',
    overflow: 'scroll',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '65px',
    padding: '0px 18px',
  },
  headerLabel: {
    fontSize: fontSize.large,
    fontWeight: 'bold ',
    opacity: '0.6',
  },
  content: {
    borderRadius: '8px',
    border: `solid 1px ${colors.border.grey1}`,
    backgroundColor: colors.lightGrey,
    '&:hover': {
      backgroundColor: colors.blue1,
    },
  },
  selected: {
    backgroundColor: `${colors.blue1} !important`,
    borderRight: `solid 5px ${colors.blue3}`,
    '&:hover': {
      backgroundColor: colors.blue1,
    },
    color: colors.blue3,
    fontWeight: 'bold',
  },
  mobileSearch_icon: {
    width: 32,
    height: 32,
    border: `solid 1px ${colors.border.grey1}`,
    backgroundColor: colors.white,
  },
  closeIcon: {
    background: 'transparent',
    height: 18,
    width: 18,
  },
  button: {
    borderRadius: '8px',
    border: `solid 1px ${colors.border.grey1}`,
    backgroundColor: colors.white,
    '&:hover': {
      border: `solid 1px ${colors.blue3}`,
      backgroundColor: colors.blue1,
    },
  },
  customStyle: {
    textTransform: 'none',
    fontSize: fontSize.smallTitle,
    margin: '2px 4px 2px 0px',
    marginRight: '8px',
  },
  icon: {
    color: colors.blue3,
    fontSize: fontSize.large,
  },
});

function filterReducer(state, { type, payload }) {
  switch (type) {
    case 'SELECT_LANGUAGE':
    case 'SELECT_SUBTITLE':
    case 'SELECT_COUNTRY':
    case 'SELECT_SEASON':
      // eslint-disable-next-line no-case-declarations
      const { aggregation_query, aggregation_type, ...existingFilters } = state;
      return {
        ...state,
        endpoint: 'productions/aggregations',
        ...existingFilters,
      };
    case 'UPDATE_DATE': {
      const new_date_from = payload.new_date_from ? payload.new_date_from : state.date_from;
      const new_date_to = payload.new_date_to ? payload.new_date_to : state.date_to;
      const localState = { ...state };
      delete localState.season_id;
      return { ...localState, date_to: new_date_to, date_from: new_date_from };
    }
    case 'UPDATE_SEASON': {
      const localState = { ...state };
      delete localState.date_from;
      delete localState.date_to;
      return { ...localState, season_id: payload.season_id };
    }
    case 'UPDATE_QUERY': {
      return {
        ...state,
        ...{
          ...payload.state,
        },
      };
    }
    case 'RESET_QUERY': {
      return {
        ...payload.state,
      };
    }
    case 'RESET_FILTERS': {
      const updatedQuery = { ...state };
      delete updatedQuery[payload];
      return {
        ...updatedQuery,
      };
    }

    default: {
      throw new Error(`Unsupported type: ${type}`);
    }
  }
}

const fetchOptionsForDropdown = (options, query, callback) => {
  const { endpoint, ...restOfQuery } = query;
  const allPromises = options.map(option => {
    if (option.type === 'date') return { [option.type]: [{ id: '-1', name: option.label }] };
    return getOptions({ endpoint, query: { ...restOfQuery, aggregation_type: option.type } }).then(data => {
      if (data?.data?.length > 0)
        if (option.type === 'year_id') {
          return { [option.type]: [...data.data] };
        } else return { [option.type]: [{ id: '-1', name: option.label }, ...data.data] };
    });
  });
  return Promise.all(allPromises).then(responses => {
    const values = {};
    // eslint-disable-next-line no-restricted-syntax,no-unused-vars
    for (const [key, value] of Object.entries(responses)) {
      // eslint-disable-next-line prefer-destructuring
      if (value) values[[Object.keys(value)]] = Object.values(value)[0];
    }
    callback(values);
    return values;
  });
};

const getSelectedValue = (state, type) => {
  return state.selectedValue[type];
};

export default function Subfilter({
  options,
  query,
  onChange,
  isSmall,
  isNewSearch,
  initialData = {},
  seasonsOptions,
  tabViewType,
  selectedValueQuery = {},
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [state, dispatch] = React.useReducer(filterReducer, {
    endpoint: 'productions/aggregations',
    ...query,
    selectedValue: {
      ...selectedValueQuery,
    },
  });

  // Mobile
  const [toggleState, setToggleState] = React.useState(false);
  const [selectedFilter, setSelectedFilter] = React.useState(options[0]);
  const [dropdownOptions, setDropdownOptions] = React.useState(initialData);

  React.useEffect(() => {
    if (query) {
      if (isNewSearch) {
        fetchOptionsForDropdown(options, query, responses => {
          setDropdownOptions(responses);
          setSelectedFilter(options.filter(item => dropdownOptions[item.type]?.length > 2 || item.type === 'date')[0]);
        });
        dispatch({
          type: 'RESET_QUERY',
          payload: {
            state: query,
          },
        });
      } else
        dispatch({
          type: 'UPDATE_QUERY',
          payload: {
            state: query,
          },
        });
    }
  }, [query]);

  const toggleDrawer = (toggleOpen, value) => event => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    if (value) setSelectedFilter(value);
    setToggleState(toggleOpen);
  };

  const toggleList = () => (
    <>
      <div className={classes.header} role="presentation">
        <p className={classes.headerLabel}>{t(`${TP}.FILTERS`)}</p>

        <IconButton
          className={classes.mobileSearch_icon}
          color="inherit"
          aria-label="close"
          onClick={() => {
            setToggleState(false);
          }}
        >
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </div>
      <div className={classes.fullList}>
        <div className={classes.list1} role="presentation">
          {options?.map((item, index) => {
            if (dropdownOptions[item.type]?.length > 2 || item.type === 'date')
              return (
                <ListItem
                  selected={item.label === selectedFilter?.label}
                  className={item.label === selectedFilter?.label ? classes.selected : classes.content}
                  button
                  key={index}
                  onClick={toggleDrawer(true, item)}
                >
                  <ListItemText primary={item.label} />
                </ListItem>
              );
          })}
        </div>
        <div className={classes.list2} role="presentation">
          {selectedFilter && (dropdownOptions[selectedFilter.type]?.length > 2 || selectedFilter.type === 'date') && (
            <FBToggleList
              query={{ aggregation_type: selectedFilter.type, ...state.query }}
              label={selectedFilter.label}
              state={{ ...state, selectedValue: selectedValueQuery }}
              dispatch={dispatch}
              type={selectedFilter.type}
              onSubmit={onChange}
              options={dropdownOptions[selectedFilter.type]?.filter(option => option.id != -1) || []}
              getSelectedValue={getSelectedValue}
              seasonsOptions={seasonsOptions}
              setToggleState={setToggleState}
            />
          )}
        </div>
      </div>
    </>
  );
  const toggleItem = (
    <>
      <Button onClick={toggleDrawer(true)} className={classnames(classes.content, classes.customStyle)}>
        <span className={classes.labelSort}>{t(`${TP}.FILTERS`)}</span>
        <SortIcon className={classes.icon} />
      </Button>
      <SwipeableDrawer anchor="bottom" open={toggleState} onClose={toggleDrawer(false)} onOpen={toggleDrawer(true)}>
        {toggleList()}
      </SwipeableDrawer>
    </>
  );

  const subFilterList = () => {
    return (
      <>
        {options?.map((item, index) => {
          return (
            <FBDropdownButton
              key={`${item.type}-${index}`}
              query={{ ...state, aggregation_type: item.type }}
              label={item.label}
              state={{ ...state, selectedValue: selectedValueQuery }}
              dispatch={dispatch}
              type={item.type}
              onSubmit={onChange}
              tabViewType={tabViewType}
              options={dropdownOptions[item.type] || []}
              getSelectedValue={getSelectedValue}
              isSmall={isSmall}
              seasonsOptions={seasonsOptions}
            />
          );
        })}
      </>
    );
  };

  return (
    <div className={classes.root}>
      {!isSmall && options.length > 0 && <span className={classes.label}>{t(`${TP}.FILTERS`)}</span>}
      {isSmall ? toggleItem : subFilterList()}
    </div>
  );
}
